import { useState, useEffect, useCallback, Fragment } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  TooltipProvider, Tooltip, TooltipTrigger, TooltipContent,
} from '../ui/tooltip';
import {
  Bot, Play, Edit, Trash2, Plus, ChevronRight, ChevronDown, CalendarClock, RefreshCw,
  Loader2, Clock, Server, ShieldCheck, Zap,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../ui/dropdown-menu';

const FREQUENCIES = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Yearly'];
const FILE_TYPES = ['Invoice', 'Statement', 'Usage'];
const BROWSERS = ['Firefox', 'Chrome', 'Edge'];

const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '\u2014';

const CollectorBotsTab = ({ accountId }) => {
  const [bots, setBots] = useState([]);
  const [linked, setLinked] = useState(true);
  const [mirror, setMirror] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  // accordion
  const [expandedId, setExpandedId] = useState(null);
  const [acctPanels, setAcctPanels] = useState({}); // { bot_id: {loading, accounts, count, error} }

  // dialogs
  const [botForm, setBotForm] = useState(null);          // create/edit
  const [scheduleFor, setScheduleFor] = useState(null);  // bot being scheduled
  const [scheduleForm, setScheduleForm] = useState({ frequency: 'Monthly', time: '10:00' });
  const [assignFor, setAssignFor] = useState(null);      // bot to add accounts
  const [assignText, setAssignText] = useState('');
  const [deleteFor, setDeleteFor] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadBots = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/ubm/accounts/${accountId}/collector-bots`);
      setBots(data?.bots || []);
      setLinked(data?.linked !== false);
      setMirror(data?.mirror === true);
    } catch {
      toast.error('Failed to load collector bots');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { loadBots(); }, [loadBots]);

  useEffect(() => {
    if (!accountId) return;
    api.get(`/ubm/accounts/${accountId}/collector-bots/suppliers`)
      .then(({ data }) => setSuppliers(data?.suppliers || []))
      .catch(() => {});
  }, [accountId]);

  const fetchAccounts = async (bot) => {
    setAcctPanels((p) => ({ ...p, [bot.bot_id]: { ...(p[bot.bot_id] || {}), loading: true } }));
    try {
      const { data } = await api.get(`/ubm/accounts/${accountId}/collector-bots/${bot.bot_id}/accounts`);
      setAcctPanels((p) => ({ ...p, [bot.bot_id]: { loading: false, accounts: data.accounts || [], count: data.count } }));
    } catch {
      setAcctPanels((p) => ({ ...p, [bot.bot_id]: { loading: false, accounts: [], error: true } }));
    }
  };

  const toggleExpand = (bot) => {
    if (expandedId === bot.bot_id) { setExpandedId(null); return; }
    setExpandedId(bot.bot_id);
    if (!acctPanels[bot.bot_id]) fetchAccounts(bot);
  };

  const supplierName = (id) => suppliers.find((s) => s.supplier_id === id)?.name;

  // ── actions ──────────────────────────────────────────────
  const handleCollect = async (bot) => {
    setBusyId(bot.bot_id);
    try {
      const { data } = await api.post(`/ubm/accounts/${accountId}/collector-bots/${bot.bot_id}/collect`);
      toast.success('Collection triggered', { description: data?.message || 'The bot will collect bills in the background.' });
    } catch (e) {
      toast.error('Could not trigger collection', { description: e?.response?.data?.detail || '' });
    } finally { setBusyId(null); }
  };

  const handleInstaBot = async (bot) => {
    setBusyId(bot.bot_id);
    try {
      await api.post(`/ubm/accounts/${accountId}/collector-bots/${bot.bot_id}/instabot`);
      toast.success('InstaBot started — fetching latest bill', {
        description: 'The most recent bill will appear in Bills once the portal login completes.',
      });
    } catch (e) {
      toast.error('Could not start InstaBot', { description: e?.response?.data?.detail || '' });
    } finally { setBusyId(null); }
  };

  const openCreate = () => setBotForm({
    _mode: 'create', username: '', password: '', supplier_id: '', account_number: '',
    status: 'Active', file_type: 'Invoice', historical_bills: 'Enabled',
    bills_period_in_months: 0, browser_type: 'Firefox',
  });

  const openEdit = (bot) => setBotForm({
    _mode: 'edit', bot_id: bot.bot_id, username: bot.username || '', password: '',
    supplier_id: bot.supplier_id || '', account_number: bot.account_number || '',
    status: bot.status || 'Active', file_type: bot.file_type || 'Invoice',
    historical_bills: bot.historical_bills || 'Enabled',
    bills_period_in_months: bot.bills_period_in_months || 0, browser_type: bot.browser_type || 'Firefox',
  });

  const saveBot = async () => {
    if (!botForm.username || !botForm.supplier_id || (botForm._mode === 'create' && !botForm.password)) {
      toast.error('Username, password and supplier are required');
      return;
    }
    setSaving(true);
    try {
      if (botForm._mode === 'create') {
        await api.post(`/ubm/accounts/${accountId}/collector-bots`, botForm);
        toast.success('Bot onboarded');
      } else {
        await api.put(`/ubm/accounts/${accountId}/collector-bots/${botForm.bot_id}`, botForm);
        toast.success('Bot updated');
      }
      setBotForm(null);
      loadBots();
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  const openSchedule = (bot) => {
    setScheduleFor(bot);
    const s = bot.schedule || {};
    setScheduleForm({
      frequency: s.frequency || 'Monthly',
      time: (s.schedule_time || '10:00:00').slice(0, 5),
    });
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      const time = scheduleForm.time.length === 5 ? `${scheduleForm.time}:00` : scheduleForm.time;
      await api.post(`/ubm/accounts/${accountId}/collector-bots/${scheduleFor.bot_id}/schedule`, {
        frequency: scheduleForm.frequency, schedule_time: time,
        is_update: !!scheduleFor.schedule, status: 'Active',
      });
      toast.success('Schedule saved');
      setScheduleFor(null);
      loadBots();
    } catch (e) {
      toast.error('Could not schedule bot', { description: e?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  const saveAssign = async () => {
    const accounts = assignText.split(/[\s,]+/).map((a) => a.trim()).filter(Boolean);
    if (!accounts.length) { toast.error('Enter at least one account number'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/ubm/accounts/${accountId}/collector-bots/${assignFor.bot_id}/assign-accounts`,
        { accounts });
      toast.success(`Accounts assigned — ${data?.count ?? accounts.length} now mapped`);
      setAcctPanels((p) => ({ ...p, [assignFor.bot_id]: { loading: false, accounts: data.accounts || [], count: data.count } }));
      setAssignFor(null); setAssignText('');
      loadBots();
    } catch (e) {
      toast.error('Could not assign accounts', { description: e?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/ubm/accounts/${accountId}/collector-bots/${deleteFor.bot_id}`);
      toast.success('Bot deactivated');
      setDeleteFor(null);
      loadBots();
    } catch (e) {
      toast.error('Could not deactivate bot', { description: e?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? bots.filter((b) => [b.supplier_name, b.bot_name, b.username, b.status]
        .some((v) => (v || '').toLowerCase().includes(q)))
    : bots;

  const statusDot = (s) => (String(s || '').toLowerCase() === 'active' ? 'bg-emerald-500' : 'bg-slate-400');

  return (
    <div data-testid="collector-bots-tab">
     <TooltipProvider>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search supplier / bot / username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-none h-9 w-72"
            data-testid="collector-bots-search"
          />
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={loadBots} data-testid="collector-bots-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <span className="text-xs text-muted-foreground">{filtered.length} bot{filtered.length === 1 ? '' : 's'}</span>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button variant="outline" size="sm" className="rounded-none" onClick={openCreate} disabled={!linked} data-testid="add-bot-btn">
                <Plus className="h-4 w-4 mr-1" /> Onboard Bot
              </Button>
            </span>
          </TooltipTrigger>
          {!linked && (
            <TooltipContent className="max-w-xs text-xs">
              This organization isn't linked to the BillPortal collector engine, so bots can't be onboarded here.
            </TooltipContent>
          )}
          {mirror && (
            <TooltipContent className="max-w-xs text-xs">
              Adds a portal login for this demo portfolio and makes it available for InstaBot (latest-bill) collection.
            </TooltipContent>
          )}
        </Tooltip>
      </div>

      {!linked && (
        <div className="mb-4 p-3 border rounded-none bg-amber-50 text-amber-800 text-sm" data-testid="collector-bots-unlinked">
          This organization isn't linked to the BillPortal collector engine, so no bots are available and new bots can't be onboarded. Onboarding requires an org mapped to a BillPortal customer.
        </div>
      )}

      <div className="border rounded-none overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Supplier</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Status</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Username</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Bot Name</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Accounts</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Last Collected</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Next Expected</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Schedule</th>
                <th className="text-left p-3 font-medium text-xs uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading bots…
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">
                  <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" /> No collector bots found.
                </td></tr>
              ) : filtered.map((bot) => {
                const isExpanded = expandedId === bot.bot_id;
                const panel = acctPanels[bot.bot_id];
                const isBusy = busyId === bot.bot_id;
                return (
                  <Fragment key={bot.bot_id}>
                    <tr className="border-t hover:bg-muted/30" data-testid={`bot-row-${bot.bot_id}`}>
                      <td className="p-3">
                        <Badge variant="outline" className="rounded-none text-xs">
                          <Server className="h-3 w-3 mr-1" />{bot.supplier_name || '\u2014'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <span className="inline-flex items-center gap-1.5" data-testid={`bot-status-${bot.bot_id}`}>
                          <span className={`h-2.5 w-2.5 rounded-full ${statusDot(bot.status)}`} />
                          <span className="text-xs text-muted-foreground">{bot.status || '\u2014'}</span>
                        </span>
                      </td>
                      <td className="p-3 font-mono text-xs">{bot.username || '\u2014'}</td>
                      <td className="p-3 text-xs">{bot.bot_name || '\u2014'}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => toggleExpand(bot)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                            data-testid={`bot-accounts-count-${bot.bot_id}`}
                          >
                            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            {bot.account_count ?? 0} account{(bot.account_count === 1) ? '' : 's'}
                          </button>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost" size="icon" className="h-6 w-6"
                                onClick={() => { setAssignFor(bot); setAssignText(''); }}
                                data-testid={`bot-add-accounts-${bot.bot_id}`}
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="text-xs">Assign account numbers to this bot</TooltipContent>
                          </Tooltip>
                        </div>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(bot.last_run_date)}</td>
                      <td className="p-3 text-xs whitespace-nowrap">
                        {bot.schedule?.next_expected ? (
                          <span className="inline-flex items-center gap-1">
                            <CalendarClock className="h-3 w-3 text-amber-600" />{fmtDate(bot.schedule.next_expected)}
                          </span>
                        ) : <span className="text-muted-foreground">{'\u2014'}</span>}
                      </td>
                      <td className="p-3">
                        {bot.schedule?.frequency ? (
                          <button
                            type="button"
                            onClick={() => openSchedule(bot)}
                            className="inline-flex items-center gap-1 text-xs hover:underline"
                            data-testid={`bot-schedule-edit-${bot.bot_id}`}
                          >
                            <Clock className="h-3 w-3 text-indigo-600" />
                            {bot.schedule.frequency} · {(bot.schedule.schedule_time || '').slice(0, 5)}
                            <Edit className="h-3 w-3 ml-0.5 opacity-60" />
                          </button>
                        ) : (
                          <Button
                            variant="outline" size="sm" className="rounded-none h-7 text-xs"
                            onClick={() => openSchedule(bot)}
                            data-testid={`bot-schedule-set-${bot.bot_id}`}
                          >
                            <Clock className="h-3 w-3 mr-1" /> Schedule
                          </Button>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 items-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline" size="sm" className="rounded-none h-8"
                                disabled={isBusy}
                                data-testid={`bot-collect-${bot.bot_id}`}
                              >
                                {isBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                                Collect <ChevronDown className="h-3 w-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-none w-60">
                              <DropdownMenuItem
                                onClick={() => handleInstaBot(bot)}
                                data-testid={`bot-instabot-${bot.bot_id}`}
                              >
                                <Zap className="h-3.5 w-3.5 mr-2 text-amber-600" />
                                InstaBot — latest bill only
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleCollect(bot)}
                                data-testid={`bot-regular-collect-${bot.bot_id}`}
                              >
                                <Play className="h-3.5 w-3.5 mr-2" />
                                Regular run — all configured bills
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => openEdit(bot)}
                            data-testid={`bot-edit-${bot.bot_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                            onClick={() => setDeleteFor(bot)}
                            data-testid={`bot-delete-${bot.bot_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-muted/20" data-testid={`bot-accounts-panel-${bot.bot_id}`}>
                        <td colSpan={9} className="p-0">
                          <div className="p-3">
                            {panel?.loading ? (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                                <RefreshCw className="h-4 w-4 animate-spin" /> Loading accounts…
                              </div>
                            ) : panel?.error ? (
                              <div className="text-sm text-muted-foreground py-4 text-center">Couldn't load accounts for this bot.</div>
                            ) : (panel?.accounts || []).length === 0 ? (
                              <div className="text-sm text-muted-foreground py-4 text-center flex flex-col items-center gap-2">
                                No accounts assigned to this bot yet.
                                <Button variant="outline" size="sm" className="rounded-none" onClick={() => { setAssignFor(bot); setAssignText(''); }} data-testid={`bot-empty-add-accounts-${bot.bot_id}`}>
                                  <Plus className="h-3.5 w-3.5 mr-1" /> Assign accounts
                                </Button>
                              </div>
                            ) : (
                              <div className="border rounded-none overflow-x-auto bg-background">
                                <table className="w-full text-sm" data-testid={`bot-accounts-table-${bot.bot_id}`}>
                                  <thead className="bg-muted/50">
                                    <tr>
                                      <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Account Number</th>
                                      <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Site</th>
                                      <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Utility Type</th>
                                      <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Frequency</th>
                                      <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Bill Source</th>
                                      <th className="text-left p-2 font-medium text-xs uppercase tracking-wide">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(panel?.accounts || []).map((a) => (
                                      <tr key={a.account_number} className="border-t" data-testid={`bot-account-row-${bot.bot_id}-${a.account_number}`}>
                                        <td className="p-2 font-mono text-xs">{a.account_number}</td>
                                        <td className="p-2">{a.site || '\u2014'}</td>
                                        <td className="p-2">{a.utility_type ? <Badge variant="outline" className="rounded-none text-xs">{a.utility_type}</Badge> : '\u2014'}</td>
                                        <td className="p-2 text-xs">{a.frequency || '\u2014'}</td>
                                        <td className="p-2 text-xs">{a.bill_source || '\u2014'}</td>
                                        <td className="p-2">
                                          <span className="inline-flex items-center gap-1.5">
                                            <span className={`h-2 w-2 rounded-full ${a.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                            <span className="text-xs">{a.status === 'active' ? 'Active' : 'Inactive'}</span>
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </TooltipProvider>

      {/* Create / Edit bot dialog */}
      <Dialog open={!!botForm} onOpenChange={(o) => !o && setBotForm(null)}>
        <DialogContent className="rounded-none max-w-lg" data-testid="bot-form-dialog">
          <DialogHeader>
            <DialogTitle>{botForm?._mode === 'edit' ? 'Edit Bot' : 'Onboard Collector Bot'}</DialogTitle>
            <DialogDescription>
              {botForm?._mode === 'edit'
                ? 'Update the portal login and collection settings for this bot.'
                : 'Create a new collector bot for this organization.'}
            </DialogDescription>
          </DialogHeader>
          {botForm && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Supplier (Collector) *</Label>
                <Select value={botForm.supplier_id} onValueChange={(v) => setBotForm((f) => ({ ...f, supplier_id: v }))}>
                  <SelectTrigger className="rounded-none" data-testid="bot-form-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {suppliers.map((s) => (<SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Username *</Label>
                <Input className="rounded-none" value={botForm.username} onChange={(e) => setBotForm((f) => ({ ...f, username: e.target.value }))} data-testid="bot-form-username" />
              </div>
              <div>
                <Label>Password {botForm._mode === 'edit' ? '(leave blank to keep)' : '*'}</Label>
                <Input type="password" className="rounded-none" value={botForm.password} onChange={(e) => setBotForm((f) => ({ ...f, password: e.target.value }))} data-testid="bot-form-password" />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input className="rounded-none" value={botForm.account_number} onChange={(e) => setBotForm((f) => ({ ...f, account_number: e.target.value }))} data-testid="bot-form-account" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={botForm.status} onValueChange={(v) => setBotForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="rounded-none" data-testid="bot-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>File Type</Label>
                <Select value={botForm.file_type} onValueChange={(v) => setBotForm((f) => ({ ...f, file_type: v }))}>
                  <SelectTrigger className="rounded-none" data-testid="bot-form-filetype"><SelectValue /></SelectTrigger>
                  <SelectContent>{FILE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Historical Bills</Label>
                <Select value={botForm.historical_bills} onValueChange={(v) => setBotForm((f) => ({ ...f, historical_bills: v }))}>
                  <SelectTrigger className="rounded-none" data-testid="bot-form-historical"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Enabled">Enabled</SelectItem>
                    <SelectItem value="Disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bills Period (months)</Label>
                <Input type="number" min={0} className="rounded-none" value={botForm.bills_period_in_months} onChange={(e) => setBotForm((f) => ({ ...f, bills_period_in_months: Number(e.target.value) }))} data-testid="bot-form-period" />
              </div>
              <div>
                <Label>Browser</Label>
                <Select value={botForm.browser_type} onValueChange={(v) => setBotForm((f) => ({ ...f, browser_type: v }))}>
                  <SelectTrigger className="rounded-none" data-testid="bot-form-browser"><SelectValue /></SelectTrigger>
                  <SelectContent>{BROWSERS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setBotForm(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveBot} disabled={saving} data-testid="bot-form-save">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {botForm?._mode === 'edit' ? 'Save Changes' : 'Onboard Bot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={!!scheduleFor} onOpenChange={(o) => !o && setScheduleFor(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="bot-schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Bot</DialogTitle>
            <DialogDescription>{scheduleFor?.bot_name}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Frequency</Label>
              <Select value={scheduleForm.frequency} onValueChange={(v) => setScheduleForm((f) => ({ ...f, frequency: v }))}>
                <SelectTrigger className="rounded-none" data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Time</Label>
              <Input type="time" className="rounded-none" value={scheduleForm.time} onChange={(e) => setScheduleForm((f) => ({ ...f, time: e.target.value }))} data-testid="schedule-time" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setScheduleFor(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveSchedule} disabled={saving} data-testid="schedule-save">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign accounts dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="bot-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign Accounts</DialogTitle>
            <DialogDescription>
              Add account numbers to <span className="font-medium">{assignFor?.bot_name}</span>. Separate multiple numbers with commas, spaces or new lines.
            </DialogDescription>
          </DialogHeader>
          <textarea
            className="w-full h-32 border rounded-none p-2 text-sm font-mono"
            placeholder={"3111351089\n2341234"}
            value={assignText}
            onChange={(e) => setAssignText(e.target.value)}
            data-testid="assign-accounts-text"
          />
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setAssignFor(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveAssign} disabled={saving} data-testid="assign-accounts-save">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="bot-delete-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate Bot</DialogTitle>
            <DialogDescription>
              There's no hard-delete for collector bots. This will set <span className="font-medium">{deleteFor?.bot_name}</span> to <span className="font-medium">Inactive</span> so it stops collecting.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDeleteFor(null)}>Cancel</Button>
            <Button variant="destructive" className="rounded-none" onClick={confirmDelete} disabled={saving} data-testid="bot-delete-confirm">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { CollectorBotsTab };
